/**
 * Endpoints proxy do backend Kotlin — Evolution e Google Calendar.
 * Client id/secret Google e API keys não ficam no bundle do front.
 */
import { apiClient } from "./client";

// ----- Google Calendar (proxy /api/google/*) ------------------------------

export type GoogleOAuthAuthorizeRequest = {
  redirect_uri: string;
};

export type GoogleOAuthAuthorizeResult = {
  ok?: boolean;
  authorize_url?: string;
};

export type GoogleOAuthExchangeRequest = {
  cnpj: string;
  code: string;
  redirect_uri: string;
  /** Agenda do colaborador (omitir = Google padrão da loja). */
  employee_id?: string;
};

export type GoogleOAuthResult = {
  ok?: boolean;
  access_token?: string;
  expires_in?: number;
  has_refresh?: boolean;
  status?: string;
  expires_at?: string;
  employee_id?: string | null;
};

export type GoogleOAuthStatusResult = {
  ok?: boolean;
  connected?: boolean;
  has_refresh?: boolean;
  access_token?: string | null;
  employee_id?: string | null;
};

export const googleProxy = {
  /** Monta a URL de consentimento Google (client_id só no backend). */
  authorizeUrl: (input: GoogleOAuthAuthorizeRequest) =>
    apiClient.post<GoogleOAuthAuthorizeResult>("/api/google/oauth/authorize", input),
  /** Troca o `code` da OAuth Google por tokens (server-side). Tokens são cifrados e ficam no backend. */
  exchangeCode: (input: GoogleOAuthExchangeRequest) =>
    apiClient.post<GoogleOAuthResult>("/api/google/oauth/exchange", input),
  /** Status da conexão Google (loja ou colaborador). */
  status: (cnpj: string, employeeId?: string | null) =>
    apiClient.get<GoogleOAuthStatusResult>("/api/google/oauth/status", {
      params: {
        cnpj,
        ...(employeeId ? { employee_id: employeeId } : {}),
      },
    }),
  /** Refresh via tokens cifrados no backend. */
  refresh: (cnpj: string, employeeId?: string | null) =>
    apiClient.post<GoogleOAuthResult>("/api/google/oauth/refresh", {
      cnpj,
      ...(employeeId ? { employee_id: employeeId } : {}),
    }),
  /** Lista eventos do calendário primário. */
  listEvents: (cnpj: string, timeMin?: string, timeMax?: string, employeeId?: string | null) =>
    apiClient.get<unknown>("/api/google/calendar/events", {
      params: {
        cnpj,
        time_min: timeMin,
        time_max: timeMax,
        ...(employeeId ? { employee_id: employeeId } : {}),
      },
    }),
  /** Cria um evento. */
  createEvent: (
    cnpj: string,
    event: Record<string, unknown>,
    employeeId?: string | null
  ) =>
    apiClient.post<unknown>(
      "/api/google/calendar/events",
      event,
      {
        params: {
          cnpj,
          ...(employeeId ? { employee_id: employeeId } : {}),
        },
      }
    ),
};

// ----- Evolution (proxy /api/evolution/*) ---------------------------------

export const evolutionProxy = {
  listChats: (instance: string) =>
    apiClient.get<unknown>("/api/evolution/chats", {
      params: { instance },
    }),
  listMessages: (instance: string, remoteJid: string, limit = 100, before?: number) =>
    apiClient.get<unknown>("/api/evolution/messages", {
      params: {
        instance,
        remoteJid,
        limit,
        ...(before != null && before > 0 ? { before } : {}),
      },
    }),
  archiveChat: (input: {
    instance: string;
    remoteJid: string;
    chatJid?: string;
    archive: boolean;
    lastMessageKey: { id: string; remoteJid: string; fromMe: boolean };
  }) => apiClient.post<{ ok?: boolean; error?: string }>("/api/evolution/archive", input),
  sendText: (input: {
    instance: string;
    to: string;
    text: string;
    notification_type?: string;
    message_category?: string;
    sender_label?: string;
    pedido_id?: number | string | null;
  }) =>
    apiClient.post<unknown>("/api/evolution/send-text", {
      cnpj: input.instance,
      instance: input.instance,
      phone: input.to,
      numero_cliente: input.to,
      to: input.to,
      text: input.text,
      mensagem: input.text,
      ...(input.notification_type
        ? { notification_type: input.notification_type }
        : {}),
      ...(input.message_category ? { message_category: input.message_category } : {}),
      ...(input.sender_label ? { sender_label: input.sender_label } : {}),
      ...(input.pedido_id != null ? { pedido_id: input.pedido_id } : {}),
    }),
  sendMedia: (formData: FormData) =>
    apiClient.post<unknown>("/api/evolution/send-media", formData, {
      headers: {}, // FormData define Content-Type sozinho
    }),
  markRead: (input: {
    instance: string;
    remoteJid: string;
    messageId: string;
    fromMe?: boolean;
  }) =>
    apiClient.post<unknown>("/api/evolution/mark-read", {
      instance: input.instance,
      remoteJid: input.remoteJid,
      messageId: input.messageId,
      from_me: input.fromMe ?? false,
    }),
  connectionState: (instance: string) =>
    apiClient.get<{ ok?: boolean; state?: string }>("/api/evolution/connection-state", {
      params: { instance },
    }),
  qrCode: (instance: string) =>
    apiClient.get<{ ok?: boolean; base64?: string; error?: string }>("/api/evolution/qr-code", {
      params: { instance },
    }),
  disconnect: (instance: string) =>
    apiClient.delete<{ ok?: boolean; error?: string }>("/api/evolution/disconnect", {
      params: { instance },
    }),
  listInstances: () =>
    apiClient.get<{ ok?: boolean; data?: unknown }>("/api/evolution/instances"),
  createInstance: (input: {
    instanceName: string;
    number: string;
    webhookUrl?: string;
  }) => apiClient.post<{ ok?: boolean; data?: unknown; error?: string }>("/api/evolution/create-instance", input),
  deleteInstance: (instance: string) =>
    apiClient.delete<{ ok?: boolean; error?: string }>("/api/evolution/instance", {
      params: { instance },
    }),
  profilePicture: (instance: string, jid: string) =>
    apiClient.post<{ ok?: boolean; profilePictureUrl?: string | null }>(
      "/api/evolution/profile-picture",
      { instance, jid }
    ),
  mediaBase64: (input: {
    instance: string;
    message: { key: Record<string, unknown> };
    convertToMp4?: boolean;
  }) =>
    apiClient.post<{ ok?: boolean; data?: unknown; error?: string }>(
      "/api/evolution/media-base64",
      input
    ),
};
